import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { categoryService, locationService, organizationService } from '../services'
import { parseDashboardIdList } from './useDashboard'

const ALL_LOCATIONS = { id: '', label: 'All Outlets' }
const ALL_CATEGORIES = { id: '', label: 'All Categories' }

const normalize = (response, allOption) => {
  const items = Array.isArray(response)
    ? response
    : response?.items || response?.data || []
  return [
    allOption,
    ...items.map((item) => ({
      id: String(item.id),
      label: item.label || item.name,
    })),
  ]
}

const normalizePartners = (response) => {
  const items = Array.isArray(response)
    ? response
    : response?.partners || response?.items || response?.data || []
  const seen = new Set()
  return items
    .map((item) => ({
      id: String(item.id),
      label: item.label || item.organization_name || item.name,
    }))
    .filter((item) => {
      if (!item.id || seen.has(item.id)) return false
      seen.add(item.id)
      return true
    })
}

const mergeSelected = (options, selectedIds, previous) => {
  const merged = [...options]
  const uniqueSelectedIds = [...new Set(selectedIds.filter(Boolean).map(String))]
  uniqueSelectedIds.forEach((selectedId) => {
    if (merged.some((option) => String(option.id) === selectedId)) return
    const selected = previous.find(
      (option) => String(option.id) === selectedId,
    )
    if (selected) merged.push(selected)
  })
  const seen = new Set()
  return merged.filter((option) => {
    const id = String(option.id ?? '')
    if (!id) return true
    if (seen.has(id)) return false
    seen.add(id)
    return true
  })
}

export function useDashboardFilterOptions(initialOptions, filters) {
  const [locations, setLocations] = useState([ALL_LOCATIONS])
  const [categories, setCategories] = useState([ALL_CATEGORIES])
  const [partners, setPartners] = useState([])
  const [locationLoading, setLocationLoading] = useState(false)
  const [categoryLoading, setCategoryLoading] = useState(false)
  const [partnerLoading, setPartnerLoading] = useState(false)
  const initialLocationsRef = useRef([ALL_LOCATIONS])
  const initialCategoriesRef = useRef([ALL_CATEGORIES])
  const initialPartnersRef = useRef([])
  const locationTimerRef = useRef()
  const categoryTimerRef = useRef()
  const partnerTimerRef = useRef()
  const locationRequestRef = useRef(0)
  const categoryRequestRef = useRef(0)
  const partnerRequestRef = useRef(0)
  const selectedLocationIds = useMemo(
    () => [
      ...parseDashboardIdList(filters.locationIds),
      filters.categoryOutletId,
      filters.heatmapOutletId,
    ],
    [filters.locationIds, filters.categoryOutletId, filters.heatmapOutletId],
  )
  const selectedCategoryIds = useMemo(
    () => [
      ...parseDashboardIdList(filters.categoryIds),
      filters.expiryCategoryId,
      filters.fastCategoryId,
    ],
    [filters.categoryIds, filters.expiryCategoryId, filters.fastCategoryId],
  )
  const selectedPartnerIds = useMemo(
    () => parseDashboardIdList(filters.organizationIds),
    [filters.organizationIds],
  )

  useEffect(() => {
    const nextLocations = normalize(initialOptions?.locations || [], ALL_LOCATIONS)
    const nextCategories = normalize(initialOptions?.categories || [], ALL_CATEGORIES)
    const nextPartnersFromDashboard = initialOptions
      ? normalizePartners(initialOptions || {})
      : null

    if (initialOptions) {
      initialLocationsRef.current = nextLocations
      initialCategoriesRef.current = nextCategories
      initialPartnersRef.current = nextPartnersFromDashboard ?? []
    }

    const partnerOptions =
      nextPartnersFromDashboard ?? initialPartnersRef.current

    setLocations((previous) =>
      mergeSelected(nextLocations, selectedLocationIds, previous),
    )
    setCategories((previous) =>
      mergeSelected(nextCategories, selectedCategoryIds, previous),
    )
    setPartners((previous) =>
      mergeSelected(partnerOptions, selectedPartnerIds, previous),
    )
  }, [
    initialOptions,
    selectedLocationIds,
    selectedCategoryIds,
    selectedPartnerIds,
  ])

  const searchLocations = useCallback(
    (value) => {
      clearTimeout(locationTimerRef.current)
      const search = value.trim()
      if (!search) {
        locationRequestRef.current += 1
        setLocationLoading(false)
        setLocations((previous) =>
          mergeSelected(
            initialLocationsRef.current,
            selectedLocationIds,
            previous,
          ),
        )
        return
      }
      const requestId = ++locationRequestRef.current
      setLocationLoading(true)
      locationTimerRef.current = setTimeout(async () => {
        try {
          const response = await locationService.getLocations({
            page: 1,
            limit: 50,
            search,
          })
          if (requestId !== locationRequestRef.current) return
          setLocations((previous) =>
            mergeSelected(
              normalize(response, ALL_LOCATIONS),
              selectedLocationIds,
              previous,
            ),
          )
        } catch (error) {
          console.error('Failed to search dashboard locations:', error)
        } finally {
          if (requestId === locationRequestRef.current) {
            setLocationLoading(false)
          }
        }
      }, 150)
    },
    [selectedLocationIds],
  )

  const searchCategories = useCallback(
    (value) => {
      clearTimeout(categoryTimerRef.current)
      const search = value.trim()
      if (!search) {
        categoryRequestRef.current += 1
        setCategoryLoading(false)
        setCategories((previous) =>
          mergeSelected(
            initialCategoriesRef.current,
            selectedCategoryIds,
            previous,
          ),
        )
        return
      }
      const requestId = ++categoryRequestRef.current
      setCategoryLoading(true)
      categoryTimerRef.current = setTimeout(async () => {
        try {
          const response = await categoryService.getCategories({
            page: 1,
            limit: 50,
            search,
          })
          if (requestId !== categoryRequestRef.current) return
          setCategories((previous) =>
            mergeSelected(
              normalize(response, ALL_CATEGORIES),
              selectedCategoryIds,
              previous,
            ),
          )
        } catch (error) {
          console.error('Failed to search dashboard categories:', error)
        } finally {
          if (requestId === categoryRequestRef.current) {
            setCategoryLoading(false)
          }
        }
      }, 150)
    },
    [selectedCategoryIds],
  )

  const searchPartners = useCallback(
    (value) => {
      clearTimeout(partnerTimerRef.current)
      const search = value.trim()
      if (!search) {
        partnerRequestRef.current += 1
        setPartnerLoading(false)
        setPartners((previous) => {
          const base =
            initialPartnersRef.current.length > 0
              ? initialPartnersRef.current
              : previous
          return mergeSelected(base, selectedPartnerIds, previous)
        })
        return
      }
      const requestId = ++partnerRequestRef.current
      setPartnerLoading(true)
      setPartners((previous) =>
        mergeSelected([], selectedPartnerIds, previous),
      )
      partnerTimerRef.current = setTimeout(async () => {
        try {
          const { items } = await organizationService.getOrganizationsPage({
            page: 1,
            limit: 50,
            search,
          })
          if (requestId !== partnerRequestRef.current) return
          setPartners((previous) =>
            mergeSelected(
              normalizePartners({ items }),
              selectedPartnerIds,
              previous,
            ),
          )
        } catch (error) {
          console.error('Failed to search dashboard partners:', error)
        } finally {
          if (requestId === partnerRequestRef.current) {
            setPartnerLoading(false)
          }
        }
      }, 150)
    },
    [selectedPartnerIds],
  )

  useEffect(
    () => () => {
      clearTimeout(locationTimerRef.current)
      clearTimeout(categoryTimerRef.current)
      clearTimeout(partnerTimerRef.current)
    },
    [],
  )

  // Pages that pass `initialOptions={null}` still need a default partner list
  // when the dropdown opens (search is empty).
  useEffect(() => {
    if (initialPartnersRef.current.length > 0) return
    let cancelled = false
    ;(async () => {
      setPartnerLoading(true)
      try {
        const { items } = await organizationService.getOrganizationsPage({
          page: 1,
          limit: 50,
        })
        if (cancelled) return
        const next = normalizePartners({ items })
        initialPartnersRef.current = next
        setPartners((previous) =>
          mergeSelected(next, selectedPartnerIds, previous),
        )
      } catch (error) {
        console.error('Failed to load default partner shops:', error)
      } finally {
        if (!cancelled) setPartnerLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time default list
  }, [])

  return {
    locations,
    categories,
    partners,
    searchLocations,
    searchCategories,
    searchPartners,
    locationLoading,
    categoryLoading,
    partnerLoading,
  }
}
